import { createActorContext } from "@xstate/react";
import { mergeWith } from "lodash";
import {
	type ActorRefFrom,
	assign,
	createActor,
	emit,
	fromPromise,
	raise,
	setup,
	stopChild,
	waitFor,
} from "xstate";

import type { DocumentMetadata } from "@moment/api-collab/api-types";

import { isDesktopAppMode } from "~/auth/interop/common";
import {
	exists,
	getDocumentDirPath,
	getDocumentsDirPath,
	getMomentGitConfigPath,
	readDir,
} from "~/data/filesystem";
import { type DocumentListItem, type Workspace } from "~/state/types";
import { getGiteaApiUrlWithoutProtocol, getWorkspaces, queryReposByWorkspace } from "~/utils/gitea";

import { filesystemApi } from "../../api/filesystem";
import { sqliteApi } from "../../api/sqlite";
import { atlasMachine } from "../atlas/atlas.machine";
import {
	type AuthenticationMachine,
	authenticationActorRef,
	waitForAccessTokenSilently,
} from "../authentication";
import { createDocumentMachine } from "../documents/create-document.machine";
import { documentMachine } from "../documents/document.machine";
import { featureFlagsMachine } from "../feature-flags/feature-flags.machine";
import { gitConfigMachine } from "../git/config/git-config.machine";
import { createQueryMachine } from "../query.machine";

const fetchWorkspacesQuery = createQueryMachine<Workspace[]>({
	queryKey: "workspaces",
	queryFn: async () => {
		const giteaHost = getGiteaApiUrlWithoutProtocol();

		const accessToken = await waitForAccessTokenSilently(authenticationActorRef);
		const workspaces = await getWorkspaces(accessToken!);

		return workspaces.map((workspace) => ({
			id: `${giteaHost}/${workspace.display_name}`,
			name: workspace.display_name,
			host: giteaHost,
			slug: workspace.display_name,
		}));
	},
	loadPersistedData: () => sqliteApi.workspaces.listAll(),
	persistData: async (data) => {
		await Promise.all(data.map(sqliteApi.workspaces.upsert));
	},
	shouldEnd: true,
});

const fetchRemoteDocumentListQuery = fromPromise<DocumentListItem[], { workspaces: Workspace[] }>(
	async ({ input }) => {
		const { workspaces } = input;

		const machines = workspaces.map((workspace) =>
			createQueryMachine<DocumentListItem[]>({
				queryKey: `documents-${workspace.id}`,
				queryFn: async () => {
					const accessToken = await waitForAccessTokenSilently(authenticationActorRef);

					const repos = await queryReposByWorkspace(accessToken!, workspace.name);

					return repos.map(
						(repo) =>
							({
								id: repo.name,
								title: repo.moment_title,
								createdAt: new Date(repo.created_at),
								updatedAt: new Date(repo.updated_at),
								workspaces: [
									{
										id: workspace.id,
										name: workspace.name,
										host: workspace.host,
										slug: workspace.slug,
									},
								],
							}) as DocumentListItem
					);
				},
				shouldEnd: true,
			})
		);

		const actors = machines.map((machine) => createActor(machine, {}));

		const documents = await Promise.allSettled(
			actors.map(async (actor) => {
				actor.start();

				const result = await waitFor(actor, (state) => state.status === "done");

				return result.output ?? [];
			})
		);

		return documents.filter((x) => x.status === "fulfilled").flatMap((x) => x.value) ?? [];
	}
);

const fetchDocumentsFromCache = fromPromise<DocumentListItem[], void>(async () => {
	return isDesktopAppMode() ? sqliteApi.documents.listAll() : [];
});

const fetchDocumentsFromDisk = fromPromise<DocumentListItem[], void>(async () => {
	if (!isDesktopAppMode()) {
		return [];
	}

	const dirPath = await getDocumentsDirPath();
	if (!(await exists(dirPath))) {
		return [];
	}

	const dirList = await readDir(dirPath).then((list) =>
		list
			// We only care about directories that are not symlinks
			.filter((file) => file.isDirectory && !file.isSymlink)
			// We don't want to include hidden files
			.filter((file) => !file.name.startsWith("."))
	);

	const documentMetadatas: Record<string, DocumentMetadata> = {};

	await Promise.all(
		dirList.map(async (dir) => {
			try {
				const documentDirPath = await getDocumentDirPath(dir.name);

				const metadata = await filesystemApi.document.metadata.read(documentDirPath);

				if (!metadata) {
					return;
				}
				documentMetadatas[documentDirPath] = metadata;
			} catch (error) {
				console.error("unable to read or parse document metadata", {
					dir: dir.name,
					error,
				});
			}
		})
	);

	return (
		Object.entries(documentMetadatas).map(
			([path, metadata]) =>
				({
					id: metadata.id,
					path,
					title: metadata.title,
					createdAt: metadata.created,
					updatedAt: metadata.created,
					workspaces: [],
				}) as DocumentListItem
		) ?? []
	);
});

const writeDocumentsToCache = fromPromise<void, { documents: DocumentListItem[] }>(
	async ({ input }) => {
		const { documents } = input;

		await Promise.all(documents.map(sqliteApi.documents.upsert));
	}
);

export type AppMachineContext = {
	workspaces: Record<string, Workspace>;
	documents: Record<string, DocumentListItem>;
	documentActorRefs: Record<string, ActorRefFrom<typeof documentMachine>>;
	authenticationActorRef: ActorRefFrom<AuthenticationMachine>;
	atlasActorRef: ActorRefFrom<typeof atlasMachine>;
	gitConfigActorRef?: ActorRefFrom<typeof gitConfigMachine>;
	featureFlagActorRef?: ActorRefFrom<typeof featureFlagsMachine>;
};

export type AppMachineInput = {
	documents: Record<string, DocumentListItem>;
};

export type AppMachineEvents =
	| { type: "workspaceList.refetch" }
	| { type: "documentList.refetch" }
	| { type: "documentList.createDocument" }
	| {
			type: "documentList.updated";
			payload: { listItems: DocumentListItem[] };
	  }
	| { type: "document.deleted"; payload: { documentId: string } };

export type AppMachineEmitted = {
	type: "document.created";
	payload: DocumentListItem;
};

export const appMachine = setup({
	types: {
		input: {} as AppMachineInput,
		context: {} as AppMachineContext,
		events: {} as AppMachineEvents,
		emitted: {} as AppMachineEmitted,
	},
	actors: {
		fetchWorkspacesQuery,
		fetchDocumentsFromCache,
		fetchDocumentsFromDisk,
		writeDocumentsToCache,
		fetchRemoteDocumentListQuery,
		createNewDocument: createDocumentMachine,
		getMomentDir: fromPromise<string>(getMomentGitConfigPath),
	},
	actions: {
		updateDocumentListItems: assign(({ context, event, spawn }) => {
			if (event.type !== "documentList.updated") {
				return context;
			}

			const newDocuments = structuredClone(context.documents);
			const newDocumentActorRefs = { ...context.documentActorRefs };

			event.payload.listItems.forEach((listItem) => {
				// Merge the workspaces from the list item with the existing document
				if (newDocuments[listItem.id]) {
					const newDocumentListItem = mergeWith(
						newDocuments[listItem.id],
						listItem,
						(a, b) => {
							if (Array.isArray(a) && Array.isArray(b)) {
								return a.concat(b);
							}

							// Don't overwrite the existing value with undefined
							if (typeof a !== "undefined" && typeof b === "undefined") {
								return a;
							}
						}
					);

					newDocuments[listItem.id] = newDocumentListItem;

					// Update the document actor with all the new data
					newDocumentActorRefs[listItem.id]?.send({
						type: "document.listItemChanged",
						payload: {
							listItem: newDocumentListItem,
						},
					});
				} else {
					// Add document to context
					newDocuments[listItem.id] = listItem;
					newDocumentActorRefs[listItem.id] = spawn(documentMachine, {
						systemId: `document:${listItem.id}`,
						input: {
							listItem,
							authenticationActorRef,
						},
					});
				}
			});

			return {
				documents: newDocuments,
				documentActorRefs: newDocumentActorRefs,
			};
		}),
	},
	guards: {
		isDesktopAppMode,
	},
}).createMachine({
	id: "app",
	type: "parallel",
	context: ({ input, spawn }) => ({
		workspaces: {},
		documents: input?.documents ?? {},
		documentActorRefs: Object.fromEntries(
			Object.entries(input?.documents ?? {}).map(([id, document]) => [
				id,
				spawn(documentMachine, {
					systemId: `document:${id}`,
					input: { listItem: document, authenticationActorRef },
				}),
			])
		),
		authenticationActorRef,
		atlasActorRef: spawn(atlasMachine),
		featureFlagActorRef: spawn(featureFlagsMachine),
	}),
	states: {
		gitConfig: {
			description: "Manage the git configuration",
			initial: "initializing",
			states: {
				initializing: {
					always: [{ guard: "isDesktopAppMode", target: "loading" }, { target: "error" }],
				},
				loading: {
					invoke: {
						src: "getMomentDir",
						onDone: {
							actions: assign(({ event, spawn }) => {
								const path = event.output;

								return {
									gitConfigActorRef: spawn(gitConfigMachine, {
										input: { path },
									}),
								};
							}),
							target: "ready",
						},
						onError: {
							target: "error",
						},
					},
				},
				ready: {},
				error: {},
			},
		},

		workspaceList: {
			description: "Manage the workspaces list",
			type: "parallel",
			states: {
				fetchWorkspaces: {
					initial: "fetching",
					states: {
						fetching: {
							invoke: {
								src: "fetchWorkspacesQuery",
								onDone: {
									target: ["done"],
									actions: [
										assign(({ context, event }) => ({
											workspaces: [
												...Object.values(context.workspaces),
												...(event.output ?? []),
											].reduce(
												(acc, workspace) => {
													acc[workspace.id] = workspace;
													return acc;
												},
												{} as Record<string, Workspace>
											),
										})),
										raise({
											type: "documentList.refetch",
										}),
									],
								},
							},
						},
						done: {
							on: {
								"workspaceList.refetch": {
									target: "fetching",
								},
							},
						},
					},
				},
			},
		},

		documentList: {
			description: "Manage the list of documents and spawn individual document actors",
			type: "parallel",
			states: {
				local: {
					initial: "fetching",
					states: {
						fetching: {
							invoke: {
								src: "fetchDocumentsFromCache",
								onDone: {
									target: "done",
									actions: raise(({ event }) => ({
										type: "documentList.updated",
										payload: { listItems: event.output ?? [] },
									})),
								},
							},
						},
						creating: {
							invoke: {
								src: "createNewDocument",
								onError: {
									actions: ({ event }) => {
										console.error("error creating document", { event });
									},
									target: "done",
								},
								onDone: {
									actions: [
										raise(({ event }) => ({
											type: "documentList.updated",
											payload: { listItems: [event.output] },
										})),
										emit(({ event }) => ({
											type: "document.created",
											payload: event.output,
										})),
									],
									target: "done",
								},
							},
						},
						done: {
							type: "final",
						},
					},
				},
				remote: {
					initial: "fetching",
					states: {
						fetching: {
							invoke: {
								src: "fetchRemoteDocumentListQuery",
								input: ({ context }) => ({
									workspaces: Object.values(context.workspaces),
								}),
								onDone: {
									actions: raise(({ event }) => ({
										type: "documentList.updated",
										payload: { listItems: event.output ?? [] },
									})),
									target: "#app.documentList.remote.done",
								},
							},
						},
						done: {
							type: "final",
						},
					},
				},
			},
			on: {
				"documentList.refetch": {
					target: [
						"#app.documentList.local.fetching",
						"#app.documentList.remote.fetching",
					],
				},
				"documentList.createDocument": {
					target: "#app.documentList.local.creating",
				},
				"document.deleted": {
					actions: [
						stopChild(
							({ context, event }) =>
								context.documentActorRefs[event.payload.documentId]!
						),
						assign(({ context, event }) => ({
							documents: Object.fromEntries(
								Object.entries(context.documents).filter(
									([id]) => id !== event.payload.documentId
								)
							),
							documentActorRefs: Object.fromEntries(
								Object.entries(context.documentActorRefs).filter(
									([id]) => id !== event.payload.documentId
								)
							),
						})),
					],
					target: [
						"#app.documentList.local.fetching",
						"#app.documentList.remote.fetching",
					],
				},
			},
		},

		disk: {
			description: "Sync the list of documents from disk to the cache",
			initial: "readingDisk",
			states: {
				readingDisk: {
					invoke: {
						src: "fetchDocumentsFromDisk",
						onDone: {
							actions: raise(({ event }) => ({
								type: "documentList.updated",
								payload: { listItems: event.output ?? [] },
							})),
							target: "writingCache",
						},
					},
				},
				writingCache: {
					invoke: {
						src: "writeDocumentsToCache",
						input: ({ context }) => ({
							// Only write documents that exist on disk to the cache
							documents: Object.values(context.documents).filter(
								(document) => document.path !== undefined
							),
						}),
						onDone: {
							target: ["done"],
						},
					},
				},
				done: {
					on: {
						"documentList.refetch": {
							target: "readingDisk",
						},
					},
				},
			},
		},
	},

	on: {
		"documentList.updated": {
			actions: {
				type: "updateDocumentListItems",
				params: ({ event }) => ({ listItems: event.payload.listItems }),
			},
		},
	},
});

export const AppActorContext: ReturnType<typeof createActorContext<typeof appMachine>> =
	createActorContext(appMachine);
export const useAppActor = AppActorContext.useActorRef;
export const useAppActorSelector = AppActorContext.useSelector;
